import { useMemo, useState } from "react";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { Modal, ConfirmDialog } from "../components/Modal.jsx";
import { EmptyState } from "../components/EmptyState.jsx";
import { TargetIcon, PlusIcon, TrashIcon, CheckIcon } from "../components/Icons.jsx";
import { fmtDate, toInputValue } from "../lib/dates.js";

const STATUSES = ["Not started", "In progress", "Blocked", "Done"];
const STATUS_TONE = {
  "Not started": "neutral", "In progress": "", Blocked: "warning", Done: "success",
};

const blankGoal = () => ({
  title: "",
  notes: "",
  status: "Not started",
  progress: 0,
  startDate: toInputValue(new Date()),
  targetDate: "",
  milestones: [],
});

export function GoalsView() {
  const { goals } = useVault();
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);

  const sorted = useMemo(
    () => [...goals].sort((a, b) => (a.targetDate || "9999").localeCompare(b.targetDate || "9999")),
    [goals]
  );

  return (
    <div className="col" style={{ gap: "var(--s-5)" }}>
      <div className="row between">
        <div className="segmented">
          <button aria-pressed={mode === "list"} onClick={() => setMode("list")}>List</button>
          <button aria-pressed={mode === "gantt"} onClick={() => setMode("gantt")}>Timeline</button>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing(blankGoal())}>
          <PlusIcon size={15} /> New goal
        </button>
      </div>

      {sorted.length === 0 ? (
        <EmptyState icon={TargetIcon} title="No goals yet"
          action={
            <button className="btn btn-primary" onClick={() => setEditing(blankGoal())}>
              <PlusIcon size={15} /> Set your first goal
            </button>
          }>
          Track what you're working towards, with milestones and a progress bar — stored
          in the same encrypted vault as everything else.
        </EmptyState>
      ) : mode === "list" ? (
        <div className="entry-grid thumbnails">
          {sorted.map((g) => <GoalCard key={g.id} goal={g} onOpen={() => setEditing(g)} />)}
        </div>
      ) : (
        <GanttChart goals={sorted} onOpen={setEditing} />
      )}

      {editing && <GoalEditor goal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function GoalCard({ goal, onOpen }) {
  const done = (goal.milestones || []).filter((m) => m.done).length;
  return (
    <button className="card goal-card" onClick={onOpen} style={{ cursor: "pointer", textAlign: "left" }}>
      <div className="goal-head">
        <h3 className="truncate">{goal.title || "Untitled goal"}</h3>
        <span className={`badge ${STATUS_TONE[goal.status] || ""}`}>{goal.status}</span>
      </div>

      <div className="progress"><span style={{ width: `${goal.progress || 0}%` }} /></div>

      <div className="row between small muted">
        <span>{goal.progress || 0}% complete</span>
        {goal.milestones?.length > 0 && <span>{done}/{goal.milestones.length} milestones</span>}
      </div>

      {goal.targetDate && (
        <div className="small secondary">Target · {fmtDate(new Date(goal.targetDate))}</div>
      )}
      {goal.notes && <p className="entry-snippet">{goal.notes}</p>}
    </button>
  );
}

function GanttChart({ goals, onOpen }) {
  const dated = goals.filter((g) => g.startDate && g.targetDate);
  if (!dated.length) {
    return <EmptyState icon={TargetIcon} title="No dated goals">
      Give a goal a start and target date to see it on the timeline.
    </EmptyState>;
  }

  const min = Math.min(...dated.map((g) => new Date(g.startDate).getTime()));
  const max = Math.max(...dated.map((g) => new Date(g.targetDate).getTime()));
  const span = Math.max(max - min, 86_400_000);
  const pct = (t) => ((new Date(t).getTime() - min) / span) * 100;

  return (
    <div className="panel">
      <div className="row between" style={{ marginBottom: "var(--s-4)" }}>
        <span className="small muted">{fmtDate(new Date(min))}</span>
        <span className="small muted">{fmtDate(new Date(max))}</span>
      </div>

      <div className="gantt">
        {dated.map((g) => {
          const left = pct(g.startDate);
          const width = Math.max(pct(g.targetDate) - left, 1.5);
          return (
            <div className="gantt-row" key={g.id}>
              <button className="gantt-label truncate" onClick={() => onOpen(g)}
                style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", textAlign: "left" }}>
                {g.title || "Untitled"}
              </button>
              <div className="gantt-track">
                <div className="gantt-bar" style={{ left: `${left}%`, width: `${width}%` }}>
                  <div className="gantt-fill" style={{ width: `${g.progress || 0}%` }} />
                </div>
                {(g.milestones || []).filter((m) => m.date).map((m, i) => (
                  <span key={i} className={`gantt-milestone ${m.done ? "done" : ""}`}
                    style={{ left: `${pct(m.date)}%` }} title={`${m.title} · ${fmtDate(new Date(m.date))}`} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GoalEditor({ goal, onClose }) {
  const { put, remove } = useVault();
  const toast = useToast();
  const [draft, setDraft] = useState(goal);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const setMilestone = (i, patch) =>
    update({ milestones: draft.milestones.map((m, j) => (j === i ? { ...m, ...patch } : m)) });

  const save = async () => {
    if (!draft.title.trim()) return toast.warn("Give the goal a title.");
    await put("goal", { ...draft, updatedAt: Date.now(), createdAt: draft.createdAt || Date.now() }, goal.id || null);
    toast.success("Goal saved");
    onClose();
  };

  return (
    <>
      <Modal title={goal.id ? "Edit goal" : "New goal"} onClose={onClose} size="wide"
        footer={
          <>
            {goal.id && (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                <TrashIcon size={15} /> Delete
              </button>
            )}
            <div className="grow" />
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save}><CheckIcon size={15} /> Save</button>
          </>
        }>
        <div className="field">
          <label htmlFor="g-title">Goal</label>
          <input id="g-title" type="text" value={draft.title} autoFocus
            onChange={(e) => update({ title: e.target.value })} placeholder="What are you working towards?" />
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="g-start">Start</label>
            <input id="g-start" type="date" value={draft.startDate || ""}
              onChange={(e) => update({ startDate: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="g-target">Target</label>
            <input id="g-target" type="date" value={draft.targetDate || ""}
              onChange={(e) => update({ targetDate: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="g-status">Status</label>
            <select id="g-status" value={draft.status} onChange={(e) => update({ status: e.target.value })}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="g-progress">Progress — {draft.progress || 0}%</label>
          <input id="g-progress" type="range" min="0" max="100" step="5" value={draft.progress || 0}
            onChange={(e) => update({ progress: Number(e.target.value) })}
            style={{ accentColor: "var(--accent)" }} />
          <div className="progress"><span style={{ width: `${draft.progress || 0}%` }} /></div>
        </div>

        <div className="field">
          <label htmlFor="g-notes">What's been achieved</label>
          <textarea id="g-notes" rows={4} value={draft.notes || ""}
            onChange={(e) => update({ notes: e.target.value })} placeholder="Notes on progress…" />
        </div>

        <div className="field">
          <div className="row between">
            <label>Milestones</label>
            <button className="btn btn-sm btn-ghost"
              onClick={() => update({ milestones: [...(draft.milestones || []), { title: "", date: "", done: false, note: "" }] })}>
              <PlusIcon size={13} /> Add
            </button>
          </div>

          <div className="col" style={{ gap: "var(--s-2)" }}>
            {(draft.milestones || []).map((m, i) => (
              <div className="row" key={i} style={{ gap: "var(--s-2)" }}>
                <label className="checkbox" style={{ flex: "none" }}>
                  <input type="checkbox" checked={!!m.done}
                    onChange={(e) => setMilestone(i, { done: e.target.checked })} />
                </label>
                <input type="text" value={m.title} placeholder="Milestone"
                  onChange={(e) => setMilestone(i, { title: e.target.value })} />
                <input type="date" value={m.date || ""} style={{ width: 160 }}
                  onChange={(e) => setMilestone(i, { date: e.target.value })} />
                <button className="icon-btn"
                  onClick={() => update({ milestones: draft.milestones.filter((_, j) => j !== i) })}
                  aria-label="Remove milestone">
                  <TrashIcon size={15} />
                </button>
              </div>
            ))}
            {!draft.milestones?.length && <p className="hint-text">No milestones yet.</p>}
          </div>
        </div>
      </Modal>

      {confirmDelete && (
        <ConfirmDialog title="Delete this goal?" danger confirmLabel="Delete"
          message="The goal and its milestones will be removed from every device you sync with."
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => { await remove(goal.id); toast.success("Goal deleted"); onClose(); }} />
      )}
    </>
  );
}
